import { Component } from '@theme/component';
import { CartAddEvent } from '@theme/events';
import { STORAGE_KEY, MAX_LISTS, MAX_ITEMS, handleFrom, cleanItem, itemKey, normalizeStore, migrateLegacy, encodeList, decodeList } from './delulu-wishlist-store.js';

const el = (tag, className = '', text = '') => {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text) node.textContent = text;
  return node;
};
const icon = (name) => {
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('viewBox', '0 0 24 24'); svg.setAttribute('fill', 'none');
  svg.setAttribute('stroke', 'currentColor'); svg.setAttribute('stroke-width', '1.3'); svg.setAttribute('aria-hidden', 'true');
  const path = document.createElementNS(svg.namespaceURI, 'path');
  path.setAttribute('d', name === 'share' ? 'M12 15V2m-4 4 4-4 4 4M7 9H4v13h16V9h-3' : 'M6 6l12 12M18 6 6 18');
  svg.append(path); return svg;
};

class DeluluWishlist extends Component {
  #controller; #observer; #store = { version: 2, lists: [] }; #copy; #cache = new Map();
  #page; #content; #dialog; #dialogContent; #opener; #title; #scroll = 0; #active = false;
  #renderToken = 0; #dialogToken = 0; #selection; #editing; #undo; #timer; #shared; #lastHash = '';

  connectedCallback() {
    super.connectedCallback();
    this.#controller?.abort(); this.#controller = new AbortController();
    const { signal } = this.#controller;
    this.#copy = JSON.parse(this.querySelector('[data-wishlist-copy]').content.textContent);
    this.#page = this.querySelector('[data-wishlist-page]'); this.#content = this.querySelector('[data-wishlist-content]');
    this.#dialog = this.querySelector('dialog'); this.#dialogContent = this.querySelector('[data-wishlist-dialog-content]');
    this.#title = document.title;
    this.#load();
    document.addEventListener('click', event => this.#documentClick(event), { signal });
    this.addEventListener('click', event => this.#click(event), { signal });
    window.addEventListener('hashchange', () => this.#route(), { signal });
    window.addEventListener('storage', event => { if (event.key === STORAGE_KEY) { this.#load(); this.#buttons(); if(this.#active) this.#render(); } }, { signal });
    this.#dialog.addEventListener('close', () => { this.#dialogToken++; if(this.#opener?.isConnected) this.#opener.focus(); }, { signal });
    document.addEventListener('variant:update', () => this.#buttons(), { signal });
    this.#observer?.disconnect();
    this.#observer = new MutationObserver(records => {
      if(records.some(record => record.target instanceof Element && record.target.matches('[data-wishlist-count],[data-open-wishlist]') || [...record.addedNodes].some(node => node instanceof Element && (node.matches('[data-save-product],[data-open-wishlist],[data-wishlist-count]') || node.querySelector('[data-save-product],[data-open-wishlist],[data-wishlist-count]'))))) this.#buttons();
    });
    this.#observer.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['hidden','aria-label'] });
    this.#buttons(); this.#route();
  }
  #t(key) { return this.#copy[key] || key; }
  #load() {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      this.#store = saved ? normalizeStore(JSON.parse(saved)) : migrateLegacy(JSON.parse(localStorage.getItem('delulu-wishlist-v1') || '[]'), this.#t('default_name'));
    } catch { this.#store = { version: 2, lists: [] }; }
  }
  #commit(next) {
    const normalized = normalizeStore(next);
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(normalized)); }
    catch { this.#error(this.#t('storage_error')); this.#notice(this.#t('storage_error')); return false; }
    this.#store = normalized; this.#buttons(); return true;
  }
  #button(text, action, className = 'dw-button') {
    const button = el('button', className, text); button.type = 'button'; button.dataset.wishAction = action; return button;
  }
  #link(text, href, className = 'dw-button') { const a = el('a', className, text); a.href = href; return a; }
  #image(item, width = 400) {
    const img = el('img'); if(item.image) img.src = item.image;
    img.alt = item.title || ''; img.width = width; img.height = width * 1.25; img.loading = 'lazy'; return img;
  }
  #productURL(item) { return `${this.dataset.root}products/${item.handle}${item.variantId ? `?variant=${item.variantId}` : ''}`; }
  #documentClick(event) {
    if (!(event.target instanceof Element)) return;
    const open = event.target.closest('[data-open-wishlist]');
    if(open) { event.preventDefault(); open.closest('dialog')?.close(); this.#opener = open; location.hash = 'wishlist'; if(this.#active) this.#route(); return; }
    const save = event.target.closest('[data-save-product]');
    if(save) {
      event.preventDefault(); event.stopPropagation();
      try {
        const item = cleanItem(JSON.parse(save.dataset.saveProduct)); if(!item) return;
        const context = save.closest('product-card, .product-details, .quick-add-modal__content');
        const variantId = context?.querySelector('input[name="id"]')?.value;
        if(variantId) item.variantId = variantId;
        this.#openSave(item, save);
      } catch { this.#notice(this.#t('load_error')); }
    }
  }
  #buttons() {
    const items = this.#store.lists.flatMap(list => list.items);
    const count = new Set(items.map(itemKey)).size;
    for(const button of document.querySelectorAll('[data-save-product]')) {
      try {
        const handle = handleFrom(JSON.parse(button.dataset.saveProduct).url);
        const saved = items.some(item => item.handle === handle);
        button.setAttribute('aria-pressed', String(saved));
        if(button.hasAttribute('data-save-icon')) button.setAttribute('aria-label', `${saved ? this.#t('manage_saved') : this.dataset.saveLabel}: ${JSON.parse(button.dataset.saveProduct).title}`);
        else button.textContent = `${saved ? '♥' : '♡'} ${saved ? this.#t('manage_saved') : this.dataset.saveLabel}`;
      } catch { /* Ignore an incomplete card during a section update. */ }
    }
    for(const button of document.querySelectorAll('[data-open-wishlist]')) {
      const label = `${this.dataset.label}${count ? ` (${count})` : ''}`;
      if (button.getAttribute('aria-label') !== label) button.setAttribute('aria-label', label);
      const badge = button.querySelector('[data-wishlist-count]'); if(badge) { if(badge.textContent !== String(count)) badge.textContent = String(count); if(badge.hidden !== !count) badge.hidden = !count; }
    }
  }
  #route() {
    const hash = location.hash;
    const active = /^#wishlist(?:\/|$)/.test(hash);
    if(active && !this.#active) this.#scroll = window.scrollY;
    this.#page.hidden = !active; document.body.classList.toggle('delulu-wishlist-open', active);
    document.title = active ? `${this.#t('title')} | Delulu Designs` : this.#title;
    if(active) { this.#active = true; this.#render(); window.scrollTo({top:0}); this.querySelector('h1').focus({preventScroll:true}); }
    else if(this.#active) { this.#active = false; this.#renderToken++; window.scrollTo({top:this.#scroll}); this.#opener?.focus({preventScroll:true}); }
    this.#lastHash = hash;
  }
  #currentList() { return this.#store.lists.find(list => `#wishlist/${list.id}` === location.hash); }
  #render() {
    const token = ++this.#renderToken; this.#content.replaceChildren(); this.#shared = null;
    this.querySelector('[data-wishlist-bottom]').hidden = false;
    if(location.hash.startsWith('#wishlist/shared/')) {
      try { this.#shared = decodeList(location.hash.slice(17)); this.#detail(this.#shared, token, true); }
      catch { this.#empty(this.#t('shared_error')); }
      return;
    }
    const list = this.#currentList();
    if(list) { this.#detail(list, token, false); return; }
    if(!this.#store.lists.length) { this.#empty(this.#t('empty')); return; }
    const grid = el('div', 'dw-list-grid');
    for(const list of this.#store.lists) {
      const card = el('article', 'dw-list-card'); card.dataset.listId = list.id;
      const share = this.#button('', 'share', 'dw-icon-button'); share.setAttribute('aria-label', `${this.#t('share')}: ${list.name}`); share.append(icon('share'));
      const preview = el('div', 'dw-list-preview');
      for(const item of list.items.slice(0,3)) if(item.image) preview.append(this.#image(item,110));
      if(!preview.children.length) preview.append(el('span', 'dw-list-placeholder', '♡'));
      const view = this.#link(this.#t('view'), `#wishlist/${list.id}`, 'dw-button dw-view-list'); view.setAttribute('aria-label', `${this.#t('view')}: ${list.name}`);
      card.append(el('h2','',list.name), el('p','',`${list.items.length} ${this.#t(list.items.length === 1 ? 'product':'products')}`), share, preview, view); grid.append(card);
    }
    this.#content.append(grid);
  }
  #empty(message) {
    const empty = el('div','dw-empty'); empty.append(el('p','',message), this.#link(`${this.#t('start')} »`,this.dataset.shop)); this.#content.append(empty);
  }
  #detail(list, token, shared) {
    const toolbar = el('div','dw-list-toolbar'); toolbar.dataset.listId = list.id;
    if(shared) toolbar.append(el('strong','',list.name),el('span','',this.#t('shared')));
    else {
      const label = el('label','',this.#t('list')); const select = el('select'); select.setAttribute('aria-label',this.#t('list'));
      for(const l of this.#store.lists) { const option = el('option','',`${l.name} (${l.items.length})`); option.value=l.id; option.selected=l.id===list.id; select.append(option); }
      select.addEventListener('change',()=>{location.hash=`wishlist/${select.value}`;}); label.append(select); toolbar.append(label);
    }
    const actions=el('div','dw-list-actions');
    for(const action of shared ? ['save_list'] : ['share','rename','delete']) actions.append(this.#button(this.#t(action),action,'dw-text-button'));
    toolbar.append(actions); this.#content.append(toolbar,this.#link(`‹ ${this.#t('back')}`,'#wishlist','dw-text-button dw-back'));
    if(!list.items.length) { this.#empty(this.#t('empty_list')); return; }
    const grid = el('div','dw-product-grid'); this.#content.append(grid);
    const jobs = list.items.map(item => {
      const card = this.#card(item,list.id,shared); grid.append(card);
      return async () => {
        try {
          const product = await this.#product(item.handle);
          if(token!==this.#renderToken || !card.isConnected) return;
          const variant = product.variants.find(v=>v.id===item.variantId);
          this.#fillCard(card,product,variant,item,list.id,shared);
        } catch {
          if(card.isConnected) { card.querySelector('[data-card-status]').textContent=this.#t('unavailable'); card.querySelector('[data-wish-action="add"]')?.remove(); }
        }
      };
    });
    // Bound requests for larger lists; section data is cached within this visit.
    const worker = async () => { while(jobs.length && token===this.#renderToken) await jobs.shift()(); };
    for(let i=0;i<Math.min(4,list.items.length);i++) worker();
  }
  #card(item,listId,shared) {
    const card=el('article','dw-product'); card.dataset.listId=listId; card.dataset.itemKey=itemKey(item);
    const imageLink=this.#link('',this.#productURL(item),'dw-product-image'); if(item.image) imageLink.append(this.#image(item));
    const title=el('h3'); title.append(this.#link(item.title,this.#productURL(item),''));
    const options=el('p','dw-variant',item.options.filter(x=>x!=='Default Title').join(' / ')); options.dataset.cardOptions='';
    const price=el('p','',item.price); price.dataset.cardPrice='';
    const status=el('p','',this.#t('loading')); status.dataset.cardStatus='';
    card.append(imageLink,title,options,price,status);
    const buy=this.#button(this.#t('loading'),'add','dw-button dw-button--solid'); buy.disabled=true; card.append(buy);
    card.append(this.#link(this.#t('shop'),this.#productURL(item),'dw-text-button'));
    if(!shared) {
      card.append(this.#button(this.#t('edit'),'edit','dw-text-button'));
      const remove=this.#button('','remove','dw-icon-button'); remove.setAttribute('aria-label',`${this.#t('remove')}: ${item.title}`); remove.append(icon('close')); card.append(remove);
    }
    return card;
  }
  #fillCard(card,product,variant,item,listId,shared) {
    const image=variant?.image || product.variants[0]?.image;
    Object.assign(item, { title: product.title, image: image || item.image, price: variant?.price || product.variants[0]?.price || '', options: variant?.options || item.options });
    if (!shared) { try { localStorage.setItem(STORAGE_KEY, JSON.stringify(this.#store)); } catch { /* Cached display data is optional; saved choices remain intact. */ } }
    const imageLink=card.querySelector('.dw-product-image');
    if(image) imageLink.replaceChildren(this.#image({image,title:product.title}));
    card.querySelector('h3 a').textContent=product.title;
    card.querySelector('[data-card-price]').textContent=variant?.price || product.variants[0]?.price || '';
    card.querySelector('[data-card-options]').textContent=variant?.options.filter(x=>x!=='Default Title').join(' / ') || this.#t('choose_options');
    card.querySelector('[data-card-status]').textContent=variant && !variant.available ? this.#t('sold_out') : '';
    const add=card.querySelector('[data-wish-action="add"]');
    add.textContent=this.#t(variant ? (variant.available ? 'add_bag':'sold_out') : 'choose_options');
    add.disabled=Boolean(variant && !variant.available);
    if(product.requiresOptions) { add.remove(); return; }
    add.onclick=()=>variant ? this.#addToBag(item,add) : this.#openSave(item,add,shared ? null : {listId,key:itemKey(item)});
  }
  async #product(handle) {
    if(this.#cache.has(handle)) return this.#cache.get(handle);
    const promise=(async()=>{
      const url=new URL(`${this.dataset.root}products/${handle}`,location.origin); url.searchParams.set('section_id','delulu-wishlist-product');
      const response=await fetch(url,{signal:this.#controller.signal}); if(!response.ok) throw new Error('Product unavailable');
      const document=new DOMParser().parseFromString(await response.text(),'text/html');
      const template=document.querySelector('[data-wishlist-product]'); if(!template) throw new Error('Product unavailable');
      const data=JSON.parse(template.content.textContent);
      if(data.handle!==handle || !Array.isArray(data.variants) || !data.variants.length) throw new Error('Product unavailable');
      return data;
    })();
    this.#cache.set(handle,promise);
    try { return await promise; } catch(error) { this.#cache.delete(handle); throw error; }
  }
  #open(title,opener) {
    this.#dialogToken++; this.#opener=opener || document.activeElement;
    this.querySelector('#DeluluWishlistDialogTitle').textContent=title; this.#dialogContent.replaceChildren(); this.#error('');
    if(!this.#dialog.open) this.#dialog.showModal();
  }
  #error(message) { const error=this.querySelector('[data-wishlist-error]'); error.textContent=message; error.hidden=!message; }
  async #openSave(item,opener,editing=null) {
    this.#open(item.title,opener); this.#editing=editing; const token=this.#dialogToken;
    this.#dialogContent.append(el('p','',this.#t('loading')));
    try {
      const product=await this.#product(item.handle); if(token!==this.#dialogToken) return;
      this.querySelector('#DeluluWishlistDialogTitle').textContent=product.title;
      const existing=this.#store.lists.flatMap(l=>l.items).find(i=>i.handle===item.handle);
      this.#selection=product.variants.find(v=>v.id===(item.variantId || existing?.variantId)) || product.variants.find(v=>v.available) || product.variants[0];
      this.#saveForm(product,editing?.listId || this.#store.lists.find(l=>l.items.some(i=>i.handle===item.handle))?.id);
    } catch {
      if(token!==this.#dialogToken) return;
      this.#dialogContent.replaceChildren(el('p','',this.#t('load_error')));
      const retry=this.#button(this.#t('retry'),'retry'); retry.onclick=()=>this.#openSave(item,opener,editing); this.#dialogContent.append(retry);
    }
  }
  #saveForm(product,selectedList) {
    this.#dialogContent.replaceChildren();
    const form=el('form'); const summary=el('div','dw-save-summary');
    const photo=this.#image({image:this.#selection.image,title:product.title},82); const price=el('p','',this.#selection.price); summary.append(photo,price); form.append(summary);
    const options=el('div'); form.append(options);
    const drawOptions=(focusIndex=null)=>{
      photo.src=this.#selection.image; price.textContent=this.#selection.price; options.replaceChildren();
      if(product.options.length===1 && product.options[0]==='Title') return;
      product.options.forEach((name,index)=>{
        const group=el('fieldset','dw-option'); group.append(el('legend','',name)); const values=el('div','dw-option-values');
        for(const value of [...new Set(product.variants.map(v=>v.options[index]))]) {
          const label=el('label','dw-option-label'); const radio=el('input'); radio.type='radio'; radio.name=`option-${index}`; radio.value=value;
          radio.checked=this.#selection.options[index]===value;
          // Sold-out variants may still be saved. Only nonexistent option combinations are skipped.
          radio.addEventListener('change',()=>{
            const next=[...this.#selection.options]; next[index]=value;
            this.#selection=product.variants.find(v=>v.options.every((x,i)=>x===next[i])) || product.variants.find(v=>v.options[index]===value && v.available) || product.variants.find(v=>v.options[index]===value);
            drawOptions(index);
          });
          label.append(radio,el('span','',value)); values.append(label);
        }
        group.append(values); options.append(group);
      });
      if(focusIndex!==null) options.querySelector(`input[name="option-${focusIndex}"]:checked`)?.focus();
    }; drawOptions();
    const label=el('label','',this.#t('save_to')); label.htmlFor='WishlistListChoice'; form.append(label);
    const select=el('select'); select.id='WishlistListChoice';
    for(const list of this.#store.lists) { const option=el('option','',list.name); option.value=list.id; option.selected=list.id===selectedList; select.append(option); }
    if(!this.#store.lists.length) { const option=el('option','',this.#t('default_name')); option.value='new-default'; select.append(option); }
    form.append(select);
    const create=this.#button(this.#t('create'),'inline-create','dw-text-button');
    const nameBox=el('div','dw-new-list'); nameBox.hidden=true; const nameLabel=el('label','',this.#t('name')); nameLabel.htmlFor='WishlistNewListName';
    const nameInput=el('input'); nameInput.type='text'; nameInput.id='WishlistNewListName'; nameInput.maxLength=60; nameBox.append(nameLabel,nameInput);
    create.onclick=()=>{nameBox.hidden=!nameBox.hidden; select.disabled=!nameBox.hidden; if(!nameBox.hidden) nameInput.focus();};
    form.append(create,nameBox);
    const actions=el('div','dw-dialog-actions'); const cancel=this.#button(this.#t('cancel'),'close'); const save=this.#button(this.#t('save'),'save','dw-button dw-button--solid'); save.type='submit'; actions.append(cancel,save); form.append(actions);
    form.addEventListener('submit',event=>{
      event.preventDefault(); this.#error(''); const next=structuredClone(this.#store);
      let list=next.lists.find(l=>l.id===select.value);
      if(!nameBox.hidden || !list) {
        const name=nameBox.hidden ? this.#t('default_name') : nameInput.value.trim();
        if(!this.#validName(name,next)) return;
        if(next.lists.length>=MAX_LISTS) {this.#error(this.#t('list_limit'));return;}
        list={id:crypto.randomUUID(),name,items:[]};next.lists.push(list);
      }
      const item=cleanItem({handle:product.handle,variantId:this.#selection.id,title:product.title,image:this.#selection.image,options:this.#selection.options,price:this.#selection.price});
      if(list.items.length>=MAX_ITEMS && !list.items.some(i=>itemKey(i)===itemKey(item))) {this.#error(this.#t('item_limit'));return;}
      if(this.#editing) { const old=next.lists.find(l=>l.id===this.#editing.listId); if(old) old.items=old.items.filter(i=>itemKey(i)!==this.#editing.key); }
      list.items=list.items.filter(i=>itemKey(i)!==itemKey(item)); list.items.unshift(item);
      if(!this.#commit(next)) return;
      this.#dialog.close(); this.#notice(this.#t('saved'));
      if(this.#active) {
        if(this.#editing && this.#editing.listId !== list.id) location.hash = `wishlist/${list.id}`;
        else this.#render();
      }
    });
    this.#dialogContent.append(form);
    options.querySelector('input:checked')?.focus();
  }
  #validName(name,next,exclude='') {
    if(!name || !name.trim()) { this.#error(this.#t('invalid_name')); return false; }
    if(next.lists.some(l=>l.id!==exclude && l.name.toLocaleLowerCase()===name.trim().toLocaleLowerCase())) {this.#error(this.#t('duplicate_name'));return false;}
    return true;
  }
  #listForm(list,opener) {
    this.#open(this.#t(list?'rename':'create'),opener);
    const form=el('form');const label=el('label','',this.#t('name'));label.htmlFor='WishlistListName';
    const input=el('input');input.type='text';input.id='WishlistListName';input.maxLength=60;input.required=true;input.value=list?.name || '';
    const actions=el('div','dw-dialog-actions');const save=this.#button(this.#t('save'),'save','dw-button dw-button--solid');save.type='submit';actions.append(this.#button(this.#t('cancel'),'close'),save);
    form.append(label,input,actions); this.#dialogContent.append(form);
    form.addEventListener('submit',event=>{
      event.preventDefault();const next=structuredClone(this.#store),name=input.value.trim();if(!this.#validName(name,next,list?.id))return;
      if(list) next.lists.find(l=>l.id===list.id).name=name;
      else {if(next.lists.length>=MAX_LISTS){this.#error(this.#t('list_limit'));return;}next.lists.push({id:crypto.randomUUID(),name,items:[]});}
      if(this.#commit(next)){this.#dialog.close();if(!this.#active)location.hash='wishlist';else this.#render();}
    }); input.focus();
  }
  #share(list,opener) {
    this.#open(this.#t('share'),opener);
    const url=new URL(this.dataset.root,location.origin); url.hash=`wishlist/shared/${encodeList(list)}`;
    const input=el('input');input.type='url';input.readOnly=true;input.value=url.href;input.setAttribute('aria-label',this.#t('share_link'));
    const button=this.#button(this.#t('copy'),'copy','dw-button dw-button--solid');
    button.onclick=async()=>{input.select();try{await navigator.clipboard.writeText(input.value);button.textContent=this.#t('copied');}catch{input.focus();input.select();}};
    const actions=el('div','dw-dialog-actions');actions.append(button);
    this.#dialogContent.append(el('p','',this.#t('share_intro')),input,actions);
  }
  #click(event) {
    const target=event.target instanceof Element ? event.target.closest('[data-wish-action]') : null;
    if(!target)return;
    const action=target.dataset.wishAction;
    const holder=target.closest('[data-list-id]');const list=this.#store.lists.find(l=>l.id===holder?.dataset.listId) || this.#currentList();
    if(action==='close')this.#dialog.close();
    if(action==='create')this.#listForm(null,target);
    if(action==='rename' && list)this.#listForm(list,target);
    if(action==='share' && list)this.#share(list,target);
    if(action==='dismiss')this.querySelector('[data-wishlist-notice]').hidden=true;
    if(action==='undo' && this.#undo){if(this.#commit(this.#undo)){this.#undo=null;this.querySelector('[data-wishlist-notice]').hidden=true;if(this.#active)this.#render();}}
    if(action==='save_list' && this.#shared){
      const next=structuredClone(this.#store);if(next.lists.length>=MAX_LISTS){this.#notice(this.#t('list_limit'));return;}
      const saved={...structuredClone(this.#shared),id:crypto.randomUUID()};next.lists.push(saved);
      if(this.#commit(next)){location.hash=`wishlist/${saved.id}`;this.#notice(this.#t('saved'));}
    }
    if(action==='delete' && list){
      this.#open(this.#t('delete'),target);this.#dialogContent.append(el('p','',this.#t('delete_question')));
      const actions=el('div','dw-dialog-actions');const confirm=this.#button(this.#t('confirm_delete'),'confirm-delete','dw-button dw-button--solid');
      confirm.onclick=()=>{const next=structuredClone(this.#store);next.lists=next.lists.filter(l=>l.id!==list.id);const before=structuredClone(this.#store);if(this.#commit(next)){this.#dialog.close();location.hash='wishlist';this.#notice(this.#t('removed'),before);}};
      actions.append(this.#button(this.#t('cancel'),'close'),confirm);this.#dialogContent.append(actions);
    }
    if((action==='edit'||action==='remove') && list){
      const card=target.closest('[data-item-key]');const item=list.items.find(i=>itemKey(i)===card?.dataset.itemKey);if(!item)return;
      if(action==='edit')this.#openSave(item,target,{listId:list.id,key:itemKey(item)});
      else{const next=structuredClone(this.#store);next.lists.find(l=>l.id===list.id).items=list.items.filter(i=>itemKey(i)!==itemKey(item));const before=structuredClone(this.#store);if(this.#commit(next)){this.#render();this.#notice(this.#t('removed'),before);this.querySelector('.dw-back')?.focus();}}
    }
  }
  async #addToBag(item,button) {
    button.disabled=true;button.textContent=this.#t('loading');
    try {
      const product=await this.#product(item.handle);const variant=product.variants.find(v=>v.id===item.variantId);
      if(!variant?.available)throw new Error('Unavailable');
      const sections=[...document.querySelectorAll('cart-items-component[data-section-id]')].map(n=>n.dataset.sectionId);
      const response=await fetch(`${this.dataset.root}cart/add.js`,{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({items:[{id:variant.id,quantity:variant.minimum || 1}],sections:[...new Set(sections)],sections_url:location.pathname}),signal:this.#controller.signal});
      const data=await response.json();if(!response.ok || data.status)throw new Error(data.description);
      this.dispatchEvent(new CartAddEvent({},variant.id,{source:'product-form-component',itemCount:variant.minimum || 1,sections:data.sections}));
      this.#notice(this.#t('added')); button.textContent=this.#t('added');
    } catch {this.#notice(this.#t('cart_error'));button.textContent=this.#t('add_bag');}
    finally {button.disabled=false;}
  }
  #notice(message,undo=null) {
    clearTimeout(this.#timer);this.#undo=undo;
    const notice=this.querySelector('[data-wishlist-notice]');notice.hidden=false;
    notice.querySelector('[data-wishlist-status]').textContent=message;notice.querySelector('[data-wish-action="undo"]').hidden=!undo;
    // Keep undo available until dismissed; short success messages fade automatically.
    if(!undo)this.#timer=setTimeout(()=>{notice.hidden=true;},6000);
  }
  disconnectedCallback() {
    this.#controller?.abort();this.#observer?.disconnect();clearTimeout(this.#timer);this.#renderToken++;this.#dialogToken++;
    document.body.classList.remove('delulu-wishlist-open');super.disconnectedCallback();
  }
}
if(!customElements.get('delulu-wishlist'))customElements.define('delulu-wishlist',DeluluWishlist);
